/** Mix buses + VoiceDirector duck gains. Pulse: music 0.25, amb 0.15, sfx 0.80, vox 1. */
export type MixBusId = 'music' | 'sfx' | 'vox' | 'amb';

export const PULSE_DUCK: Record<MixBusId, number> = {
  music: 0.25,
  sfx: 0.8,
  vox: 1,
  amb: 0.15,
};

const UNITY: Record<MixBusId, number> = {
  music: 1,
  sfx: 1,
  vox: 1,
  amb: 1,
};

export class Buses {
  readonly master: GainNode;
  readonly music: GainNode;
  readonly sfx: GainNode;
  readonly vox: GainNode;
  readonly amb: GainNode;
  readonly musicDuck: GainNode;
  readonly sfxDuck: GainNode;
  readonly voxDuck: GainNode;
  readonly ambDuck: GainNode;

  constructor(ctx: BaseAudioContext) {
    this.master = ctx.createGain();
    this.music = ctx.createGain();
    this.sfx = ctx.createGain();
    this.vox = ctx.createGain();
    this.amb = ctx.createGain();
    this.musicDuck = ctx.createGain();
    this.sfxDuck = ctx.createGain();
    this.voxDuck = ctx.createGain();
    this.ambDuck = ctx.createGain();

    this.music.connect(this.musicDuck).connect(this.master);
    this.sfx.connect(this.sfxDuck).connect(this.master);
    this.vox.connect(this.voxDuck).connect(this.master);
    this.amb.connect(this.ambDuck).connect(this.master);
  }

  input(id: MixBusId): GainNode {
    return this[id];
  }

  duckNode(id: MixBusId): GainNode {
    switch (id) {
      case 'music':
        return this.musicDuck;
      case 'sfx':
        return this.sfxDuck;
      case 'vox':
        return this.voxDuck;
      case 'amb':
        return this.ambDuck;
    }
  }

  duck(amounts: Partial<Record<MixBusId, number>>, at = 0, timeConstant = 0.05): void {
    this.apply(this.musicDuck, amounts.music, at, timeConstant);
    this.apply(this.sfxDuck, amounts.sfx, at, timeConstant);
    this.apply(this.voxDuck, amounts.vox, at, timeConstant);
    this.apply(this.ambDuck, amounts.amb, at, timeConstant);
  }

  applyPulseDuck(on: boolean, at = 0): void {
    this.duck(on ? PULSE_DUCK : UNITY, at);
  }

  private apply(node: GainNode, value: number | undefined, at: number, timeConstant: number): void {
    if (value === undefined) {
      return;
    }
    node.gain.setTargetAtTime(value, at, timeConstant);
  }
}
